import React, { useState, useEffect } from 'react';
import { DollarSign, X, Info, Building2, User, AlertTriangle, Camera, FileText, MessageCircle, Trash2 } from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { readFileAsBase64, validateFile } from '../../../utils/helpers';
import telemetry from '../../../services/telemetry';

const AddExpense = ({ games, tournaments, teams = [], onBack, onAddExpense, onUpdateExpense, onDeleteExpense, initialData, isEditMode, currentUser, players = [] }) => {
    const [expenseType, setExpenseType] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(''); // NEW: For dropdown state
    const [expenseAmount, setExpenseAmount] = useState('');
    const [expenseDate, setExpenseDate] = useState('');
    const [expenseGame, setExpenseGame] = useState('');
    const [expenseTournament, setExpenseTournament] = useState('');
    const [expenseReceipt, setExpenseReceipt] = useState(null);
    const [expenseNote, setExpenseNote] = useState('');
    const [expenseReceiptPreview, setExpenseReceiptPreview] = useState('');
    const [loading, setLoading] = useState(false);

    const [paidBy, setPaidBy] = useState('player'); // 'player' or 'club'
    const [splitType, setSplitType] = useState('squad'); // 'squad', 'tournament', or 'club'
    const [customPayerId, setCustomPayerId] = useState(''); // Allows admin to override who paid

    // Track squad details for the selected game (for squad split)
    const [squadSize, setSquadSize] = useState(0);
    const [superSubCount, setSuperSubCount] = useState(0);
    const [regularPlayerCount, setRegularPlayerCount] = useState(0);
    const [perPlayerCost, setPerPlayerCost] = useState(0);
    const [perSuperSubCost, setPerSuperSubCost] = useState(0);

    // Standard expense categories
    const DEFAULT_CATEGORIES = ['Tournament Fee', 'Food', 'Ground Fee', 'Umpiring', 'Equipment', 'Transport', 'Other'];
    const [availableCategories, setAvailableCategories] = useState(DEFAULT_CATEGORIES);
    const [saveCategory, setSaveCategory] = useState(false);

    // Fetch saved categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const docRef = doc(db, 'settings', 'expenseTypes');
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().categories) {
                    // Merge defaults with saved, deduplicate
                    const saved = docSnap.data().categories;
                    const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...saved]));
                    setAvailableCategories(merged.sort()); // Sort alphabetically
                }
            } catch (err) {
                console.error("Error fetching expense categories:", err);
            }
        };

        fetchCategories();
    }, []);

    // Get player name from currentUser
    const playerName = currentUser?.displayName || currentUser?.name || currentUser?.email?.split('@')[0] || 'Player';

    useEffect(() => {
        if (!isEditMode && !customPayerId && currentUser?.uid) {
            setCustomPayerId(currentUser.uid);
        }
    }, [currentUser?.uid, isEditMode]);

    // Construct a bulletproof extended roster of all players who have played any games 
    // to bypass cases where players are hidden from the primary roster due to admin status
    const extendedPlayers = React.useMemo(() => {
        const playerMap = new Map();
        
        // Add primary loaded players
        if (players) {
            players.forEach(p => playerMap.set(p.id || p.email, { id: p.id, name: p.name, email: p.email }));
        }

        // Add any orphaned players nested inside game squads
        if (games) {
            games.forEach(game => {
                const allGamePlayers = [...(game.playing11 || []), ...(game.superSubs || [])];
                allGamePlayers.forEach(p => {
                    if (p && (p.id || p.email) && !playerMap.has(p.id) && !playerMap.has(p.email)) {
                        playerMap.set(p.id || p.email, { id: p.id, name: p.name, email: p.email });
                    }
                });
            });
        }

        // Add any orphaned players from native team rosters
        if (teams) {
            teams.forEach(team => {
                if (team.players) {
                    team.players.forEach(p => {
                        if (p && (p.id || p.email) && !playerMap.has(p.id) && !playerMap.has(p.email)) {
                            playerMap.set(p.id || p.email, { id: p.id, name: p.name, email: p.email });
                        }
                    });
                }
            });
        }
        
        return Array.from(playerMap.values()).sort((a, b) => (a.name || '').toString().localeCompare((b.name || '').toString()));
    }, [players, games, teams]);

    // Populate state if editing
    useEffect(() => {
        if (initialData && isEditMode) {
            const expType = initialData.type || initialData.description || '';
            setExpenseType(expType);
            setExpenseAmount(initialData.amount ? initialData.amount.toString() : '');
            
            // Handle date formatting
            let expDate = initialData.date || '';
            if (!expDate && initialData.createdAt) {
                expDate = new Date(initialData.createdAt).toISOString().split('T')[0];
            }
            if (expDate.includes('/')) {
                // If MM/DD/YYYY, convert to YYYY-MM-DD
                const parts = expDate.split('/');
                if (parts.length === 3) {
                    expDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
                }
            }
            setExpenseDate(expDate);
            
            setExpenseGame(initialData.gameId || '');
            setExpenseTournament(initialData.tournamentId || '');
            setExpenseReceiptPreview(initialData.receiptURL || initialData.receipt || '');
            setPaidBy(initialData.paidBy || (initialData.addedByPlayerId || initialData.addedByPlayerName ? 'player' : 'club'));
            
            let resolvedPayerId = initialData.addedByPlayerId || initialData.paidByAdminId || null;
            if (!resolvedPayerId) {
                // Try strictly mapping legacy email or Name to current players list in case IDs were missed
                const match = extendedPlayers.find(p => 
                    (initialData.createdBy && (p.email === initialData.createdBy || p.id === initialData.createdBy)) ||
                    (initialData.addedByPlayerName && p.name === initialData.addedByPlayerName) ||
                    (initialData.paidByAdminName && p.name === initialData.paidByAdminName)
                );
                if (match) resolvedPayerId = match.id;
            }

            if (resolvedPayerId) {
                setCustomPayerId(resolvedPayerId);
            } else if (initialData.createdBy || initialData.addedByPlayerName || initialData.paidByAdminName) {
                // Fallback for authors who no longer exist in the players array
                setCustomPayerId('LEGACY_AUTHOR');
            }
            
            // Determine split logic
            let split = 'squad';
            if (initialData.splitType) {
                split = initialData.splitType;
            } else if (initialData.isDividable !== undefined) {
                 split = initialData.isDividable ? 'squad' : (initialData.isTournamentFee ? 'tournament' : 'club');
            } else if (initialData.clubCovered) {
                 split = 'club';
            }
            setSplitType(split);

            // Determine category dropdown
            if (DEFAULT_CATEGORIES.includes(expType) || availableCategories.includes(expType)) {
                setSelectedCategory(expType);
            } else {
                setSelectedCategory('Other');
            }
        }
    }, [initialData, isEditMode, availableCategories]);


    // Get games for the selected tournament
    const tournamentGames = expenseTournament
        ? games?.filter(g => g.tournamentId === expenseTournament) || []
        : [];

    // Update squad details when game is selected (for squad split type)
    useEffect(() => {
        if (expenseGame && games && splitType === 'squad') {
            const selectedGame = games.find(g => g.id === expenseGame);
            if (selectedGame && selectedGame.playing11) {
                const totalSquad = selectedGame.playing11.length;
                const superSubs = selectedGame.superSubs || [];
                const superSubsCount = superSubs.length;
                const regularPlayers = totalSquad - superSubsCount;

                setSquadSize(totalSquad);
                setSuperSubCount(superSubsCount);
                setRegularPlayerCount(regularPlayers);
            } else {
                setSquadSize(0);
                setSuperSubCount(0);
                setRegularPlayerCount(0);
            }
        } else {
            setSquadSize(0);
            setSuperSubCount(0);
            setRegularPlayerCount(0);
        }
    }, [expenseGame, games, splitType]);

    // Calculate per-player cost when amount or squad changes (for squad split)
    useEffect(() => {
        if (expenseAmount && squadSize > 0 && splitType === 'squad') {
            const amount = parseFloat(expenseAmount);

            // Calculate effective shares: regular players = 1 share each, 2 super subs = 1 share total
            // So if we have 10 regular + 2 super subs = 10 + 0.5 + 0.5 = 11 shares
            const effectiveShares = regularPlayerCount + (superSubCount / 2);

            if (effectiveShares > 0) {
                const costPerShare = amount / effectiveShares;
                setPerPlayerCost(costPerShare); // Regular player pays full share
                setPerSuperSubCost(costPerShare / 2); // Super sub pays half share
            } else {
                setPerPlayerCost(0);
                setPerSuperSubCost(0);
            }
        } else {
            setPerPlayerCost(0);
            setPerSuperSubCost(0);
        }
    }, [expenseAmount, squadSize, regularPlayerCount, superSubCount, splitType]);


    const handleReceiptUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validation = validateFile(file);
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        setExpenseReceipt(file);
        const base64 = await readFileAsBase64(file);
        setExpenseReceiptPreview(base64);
    };

    const handleSubmit = async () => {
        if (!expenseType || !expenseAmount || !expenseDate) {
            alert('Please fill all required fields');
            return;
        }

        // Validation based on split type
        if (splitType === 'squad' && !expenseGame) {
            alert('Please select a game for squad-split expenses');
            return;
        }

        if (splitType === 'squad' && squadSize === 0) {
            alert('The selected game must have a playing squad selected');
            return;
        }

        if (splitType === 'tournament' && !expenseTournament) {
            alert('Please select a tournament for tournament-level split');
            return;
        }

        setLoading(true);

        let receiptData = null;
        if (expenseReceipt) {
            receiptData = await readFileAsBase64(expenseReceipt);
        }

        const expenseData = {
            type: expenseType,
            amount: parseFloat(expenseAmount),
            date: expenseDate,
            gameId: splitType === 'squad' ? expenseGame : null,
            tournamentId: expenseTournament || null,
            receipt: receiptData,
            receiptName: expenseReceipt?.name || null,

            // New flexible fields
            paidBy: paidBy, // 'player' or 'club'
            splitType: splitType, // 'squad', 'tournament', or 'club'

            // For reimbursement tracking (when player paid)
            addedByPlayerId: paidBy === 'player' ? (customPayerId === 'LEGACY_AUTHOR' ? (initialData?.addedByPlayerId || initialData?.paidByAdminId || null) : (customPayerId || currentUser?.uid)) : null,
            addedByPlayerName: paidBy === 'player' ? (customPayerId === 'LEGACY_AUTHOR' ? (initialData?.addedByPlayerName || initialData?.paidByAdminName || initialData?.createdByName || initialData?.createdBy || 'Unknown') : (extendedPlayers.find(p => p.id === customPayerId)?.name || playerName)) : null,
            paidByAdminId: paidBy === 'player' ? (customPayerId === 'LEGACY_AUTHOR' ? (initialData?.paidByAdminId || initialData?.addedByPlayerId || null) : (customPayerId || currentUser?.uid)) : null,
            paidByAdminName: paidBy === 'player' ? (customPayerId === 'LEGACY_AUTHOR' ? (initialData?.paidByAdminName || initialData?.addedByPlayerName || initialData?.createdByName || initialData?.createdBy || 'Unknown') : (extendedPlayers.find(p => p.id === customPayerId)?.name || playerName)) : null,
            reimbursementStatus: paidBy === 'player' ? (initialData?.reimbursementStatus || 'pending') : null,

            // Squad-level split data (for splitType === 'squad')
            squadSize: splitType === 'squad' ? squadSize : null,
            superSubCount: splitType === 'squad' ? superSubCount : null,
            regularPlayerCount: splitType === 'squad' ? regularPlayerCount : null,
            perPlayerCost: splitType === 'squad' ? perPlayerCost : null,
            perSuperSubCost: splitType === 'squad' ? perSuperSubCost : null,

            // Legacy compatibility fields
            isDividable: splitType === 'squad',
            isTournamentFee: splitType === 'tournament',
            clubCovered: splitType === 'club',
            settled: false,
        };

        if (expenseNote && expenseNote.trim() !== '') {
            const newNote = {
                text: expenseNote.trim(),
                addedBy: paidBy === 'player' ? playerName : (currentUser?.displayName || currentUser?.email || 'Admin'),
                addedAt: new Date().toISOString()
            };
            if (isEditMode) {
                expenseData.notes = arrayUnion(newNote);
            } else {
                expenseData.notes = [newNote];
            }
        }

        // Save new category if requested
        if (saveCategory && selectedCategory === 'Other' && expenseType) {
            try {
                const docRef = doc(db, 'settings', 'expenseTypes');
                await setDoc(docRef, {
                    categories: arrayUnion(expenseType)
                }, { merge: true });

                // Update local state immediately
                setAvailableCategories(prev => Array.from(new Set([...prev, expenseType])).sort());
            } catch (err) {
                console.error("Error saving new category:", err);
                // Continue with expense creation even if saving category fails
            }
        }

        if (isEditMode && onUpdateExpense) {
            await onUpdateExpense({ ...initialData, ...expenseData });
        } else {
            await onAddExpense(expenseData);
        }

        // TELEMETRY: Log expense added
        if (telemetry) {
            telemetry.logEvent('expense_added', {
                type: expenseType,
                amount: parseFloat(expenseAmount),
                splitType: splitType
            });
        }

        // Reset form
        setExpenseType('');
        setExpenseAmount('');
        setExpenseDate('');
        setExpenseGame('');
        setExpenseTournament('');
        setExpenseReceipt(null);
        setExpenseReceiptPreview('');
        setExpenseNote('');
        setPaidBy('player');
        setCustomPayerId(currentUser?.uid || '');
        setSplitType('squad');
        setSquadSize(0);
        setSuperSubCount(0);
        setRegularPlayerCount(0);
        setPerPlayerCost(0);
        setPerSuperSubCost(0);
        setSaveCategory(false);
        setSelectedCategory('');
        setLoading(false);

        // Notify parent to maybe switch tabs or give feedback
        alert('Expense added successfully!');
    };

    return (
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                    <DollarSign className="text-orange-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold mb-2">{isEditMode ? 'Edit Expense' : 'Add Expense'}</h2>
                <p className="text-gray-600">{isEditMode ? 'Update expense details' : 'Record a new expense'}</p>
            </div>

            <div className="space-y-4">
                {/* Expense Type - Conditional: Text for Club, Dropdown for Squad/Tournament */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expense Type *
                    </label>

                    {splitType === 'club' ? (
                        /* Free text for Club expenses */
                        <input
                            type="text"
                            value={expenseType}
                            onChange={(e) => setExpenseType(e.target.value)}
                            placeholder="e.g., Annual Domain Renewal, Trophy Purchase"
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                            disabled={loading}
                        />
                    ) : (
                        /* Dropdown for Tournament/Squad expenses */
                        <div className="space-y-3">
                            <select
                                value={selectedCategory}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedCategory(val);
                                    setSaveCategory(false); // Reset checkbox
                                    if (val !== 'Other') {
                                        setExpenseType(val);
                                    } else {
                                        setExpenseType(''); // Clear for manual entry
                                    }
                                }}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                disabled={loading}
                            >
                                <option value="">Select category...</option>
                                {availableCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>

                            {/* Show text input if "Other" is selected */}
                            {selectedCategory === 'Other' && (
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        value={expenseType}
                                        onChange={(e) => setExpenseType(e.target.value)}
                                        placeholder="Specify expense details..."
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-gray-50"
                                        disabled={loading}
                                        autoFocus
                                    />

                                    {/* Option to save this new category */}
                                    {expenseType && (
                                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={saveCategory}
                                                onChange={(e) => setSaveCategory(e.target.checked)}
                                                className="rounded text-orange-600 focus:ring-orange-500"
                                            />
                                            Add "{expenseType}" to saved categories list
                                        </label>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Paid By Selection */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Paid By *
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setPaidBy('player')}
                            disabled={loading}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${paidBy === 'player'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <User className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold">
                                    {customPayerId === currentUser?.uid 
                                        ? `Me (${playerName})` 
                                        : (players?.find(p => p.id === customPayerId)?.name || 'Player')}
                                </span>
                            </div>
                            <p className="text-xs text-gray-600">I paid out of pocket</p>
                        </button>

                        <button
                            type="button"
                            onClick={() => setPaidBy('club')}
                            disabled={loading}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${paidBy === 'club'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Building2 className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold">Club Account</span>
                            </div>
                            <p className="text-xs text-gray-600">Club paid directly</p>
                        </button>
                    </div>
                    
                    {paidBy === 'player' && players && players.length > 0 && (
                        <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Player who paid
                            </label>
                            <select
                                value={customPayerId || currentUser?.uid || ''}
                                onChange={(e) => setCustomPayerId(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 text-sm"
                            >
                                <option value={currentUser?.uid}>Me ({playerName})</option>
                                {customPayerId === 'LEGACY_AUTHOR' && (
                                    <option value="LEGACY_AUTHOR">Original Submitter ({initialData?.addedByPlayerName || initialData?.paidByAdminName || initialData?.createdByName || initialData?.createdBy || 'Unknown'})</option>
                                )}
                                {customPayerId && customPayerId !== 'LEGACY_AUTHOR' && customPayerId !== currentUser?.uid && !extendedPlayers.some(p => p.id === customPayerId) && (
                                    <option value={customPayerId}>{initialData?.paidByAdminName || initialData?.addedByPlayerName || 'Former Member'}</option>
                                )}
                                {extendedPlayers.filter(p => p.id !== currentUser?.uid).map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Split Type Selection */}
                < div >
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        How should this expense be split? *
                    </label>
                    <div className="space-y-2">
                        <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${splitType === 'squad'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                value="squad"
                                checked={splitType === 'squad'}
                                onChange={(e) => setSplitType(e.target.value)}
                                disabled={loading}
                                className="w-4 h-4 mt-0.5"
                            />
                            <div>
                                <span className="font-semibold text-sm">Split among playing team</span>
                                <p className="text-xs text-gray-600 mt-1">Split per player in this game's squad</p>
                            </div>
                        </label>

                        <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${splitType === 'tournament'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                value="tournament"
                                checked={splitType === 'tournament'}
                                onChange={(e) => setSplitType(e.target.value)}
                                disabled={loading}
                                className="w-4 h-4 mt-0.5"
                            />
                            <div>
                                <span className="font-semibold text-sm">Split at tournament level</span>
                                <p className="text-xs text-gray-600 mt-1">Split across all players who played any game in the tournament</p>
                            </div>
                        </label>

                        <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${splitType === 'club'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                value="club"
                                checked={splitType === 'club'}
                                onChange={(e) => setSplitType(e.target.value)}
                                disabled={loading}
                                className="w-4 h-4 mt-0.5"
                            />
                            <div>
                                <span className="font-semibold text-sm">Club/Tournament expense</span>
                                <p className="text-xs text-gray-600 mt-1">Club bears full cost, not split among players</p>
                            </div>
                        </label>
                    </div>
                </div >

                {/* Game Selection - Only for squad split */}
                {
                    splitType === 'squad' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Game *
                            </label>
                            <select
                                value={expenseGame}
                                onChange={(e) => setExpenseGame(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                disabled={loading}
                            >
                                <option value="">Select game</option>
                                {games && games.length > 0 ? (
                                    games.map(game => (
                                        <option key={game.id} value={game.id}>
                                            {game.opponent} - {game.date}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>No games available</option>
                                )}
                            </select>
                            {squadSize > 0 && (
                                <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                    <p className="text-sm text-gray-700">
                                        Squad: {squadSize} players ({regularPlayerCount} regular, {superSubCount} super subs)
                                    </p>
                                </div>
                            )}
                        </div>
                    )
                }

                {/* Tournament Selection - Only for tournament split */}
                {
                    splitType === 'tournament' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tournament *
                            </label>
                            <select
                                value={expenseTournament}
                                onChange={(e) => setExpenseTournament(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                disabled={loading}
                            >
                                <option value="">Select tournament</option>
                                {tournaments && tournaments.length > 0 ? (
                                    tournaments.map(tournament => (
                                        <option key={tournament.id} value={tournament.id}>
                                            {tournament.name} {tournament.date ? `- ${tournament.date}` : ''}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>No tournaments available</option>
                                )}
                            </select>
                        </div>
                    )
                }

                {/* For Club expenses - show BOTH game and tournament as optional for audit trail */}
                {
                    splitType === 'club' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Associated Game (Optional)
                                </label>
                                <select
                                    value={expenseGame}
                                    onChange={(e) => setExpenseGame(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                    disabled={loading}
                                >
                                    <option value="">Select game (optional)</option>
                                    {games && games.length > 0 ? (
                                        games.map(game => (
                                            <option key={game.id} value={game.id}>
                                                {game.opponent} - {game.date}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>No games available</option>
                                    )}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Associate this expense with a specific game for audit purposes
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Associated Tournament (Optional)
                                </label>
                                <select
                                    value={expenseTournament}
                                    onChange={(e) => setExpenseTournament(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                    disabled={loading}
                                >
                                    <option value="">Select tournament (optional)</option>
                                    {tournaments && tournaments.length > 0 ? (
                                        tournaments.map(tournament => (
                                            <option key={tournament.id} value={tournament.id}>
                                                {tournament.name} {tournament.date ? `- ${tournament.date}` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>No tournaments available</option>
                                    )}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Associate this expense with a specific tournament for audit purposes
                                </p>
                            </div>
                        </>
                    )
                }

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount ($) *
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        value={expenseAmount}
                        onChange={(e) => setExpenseAmount(e.target.value)}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="150.00"
                        disabled={loading}
                    />
                    {/* Squad split breakdown */}
                    {splitType === 'squad' && expenseAmount && squadSize > 0 && (
                        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-sm text-gray-700 mb-1">
                                Total: <span className="font-semibold">${parseFloat(expenseAmount).toFixed(2)}</span>
                            </p>
                            <div className="text-sm space-y-1">
                                {regularPlayerCount > 0 && (
                                    <p>
                                        <span className="text-gray-600">{regularPlayerCount} regular player{regularPlayerCount !== 1 ? 's' : ''}</span>
                                        <span className="font-semibold text-green-700 ml-2">
                                            ${perPlayerCost.toFixed(2)} each
                                        </span>
                                    </p>
                                )}
                                {superSubCount > 0 && (
                                    <p>
                                        <span className="text-gray-600">{superSubCount} super sub{superSubCount !== 1 ? 's' : ''}</span>
                                        <span className="font-semibold text-green-700 ml-2">
                                            ${perSuperSubCost.toFixed(2)} each
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                    </label>
                    <input
                        type="date"
                        value={expenseDate}
                        onChange={(e) => setExpenseDate(e.target.value)}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                        disabled={loading}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Receipt (Optional)
                    </label>

                    {!expenseReceiptPreview ? (
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => document.getElementById('receipt-upload-admin').click()}
                                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all hover:scale-[1.02]"
                            >
                                <Camera size={28} className="mb-2" />
                                <span className="text-sm font-semibold">Camera</span>
                            </button>

                            <div className="relative">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleReceiptUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    id="receipt-upload-admin"
                                />
                                <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 transition-all h-full hover:scale-[1.02]">
                                    <div className="mb-2 p-1 bg-white rounded-lg shadow-sm">
                                        <FileText size={24} className="text-gray-500" />
                                    </div>
                                    <span className="text-sm font-semibold">Gallery/Files</span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <p className="text-xs text-gray-500 mt-2">
                        Max file size: 5MB. Supported formats: JPG, PNG, GIF
                    </p>

                    {expenseReceiptPreview && (
                        <div className="mt-4 relative">
                            <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                            <div className="relative inline-block">
                                <img
                                    src={expenseReceiptPreview}
                                    alt="Receipt preview"
                                    className="max-w-full h-48 object-contain border rounded-lg"
                                />
                                <button
                                    onClick={() => {
                                        setExpenseReceipt(null);
                                        setExpenseReceiptPreview('');
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                                    disabled={loading}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary before submit */}
                {
                    expenseType && expenseAmount && (
                        <div className="p-4 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                            <h4 className="font-semibold text-gray-800 mb-2">Summary</h4>
                            <div className="text-sm space-y-1">
                                <p><span className="text-gray-600">Type:</span> <span className="font-medium">{expenseType}</span></p>
                                <p><span className="text-gray-600">Amount:</span> <span className="font-medium">${parseFloat(expenseAmount).toFixed(2)}</span></p>
                                <p>
                                    <span className="text-gray-600">Paid by:</span>{' '}
                                    <span className={`font-medium ${paidBy === 'club' ? 'text-green-700' : 'text-blue-700'}`}>
                                        {paidBy === 'club' ? '🏢 Club Account' : `👤 ${customPayerId === currentUser?.uid ? playerName : (players?.find(p => p.id === customPayerId)?.name || 'Player')}`}
                                    </span>
                                </p>
                                {paidBy === 'player' && (
                                    <p className="text-amber-600 text-xs">💰 Pending reimbursement</p>
                                )}
                                {splitType === 'squad' && squadSize > 0 && (
                                    <p className="text-green-600 text-xs">✓ Split among {squadSize} players</p>
                                )}
                                {splitType === 'tournament' && (
                                    <p className="text-purple-600 text-xs">📋 Split at tournament level</p>
                                )}
                                {splitType === 'club' && (
                                    <p className="text-green-600 text-xs">🏢 Club expense - not split</p>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Expense Notes Section */}
                <div className="border-t pt-4 mt-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <MessageCircle size={20} className="text-gray-500" />
                        Expense Notes & Activity
                    </h3>

                    {/* Existing Notes Feed */}
                    {isEditMode && initialData?.notes && (
                        <div className="space-y-3 mb-4 max-h-48 overflow-y-auto pr-2">
                            {Array.isArray(initialData.notes) ? (
                                initialData.notes.map((note, index) => (
                                    <div key={index} className="bg-gray-50 rounded-lg p-3 text-sm border">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="font-semibold text-gray-700">{note.addedBy}</span>
                                            <span className="text-xs text-gray-500">
                                                {note.addedAt ? new Date(note.addedAt).toLocaleString() : ''}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 whitespace-pre-wrap">{note.text}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-3 text-sm border">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-semibold text-gray-700">System Note</span>
                                    </div>
                                    <p className="text-gray-600 whitespace-pre-wrap">{initialData.notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Add New Note */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Add a Note (Optional)
                        </label>
                        <textarea
                            value={expenseNote}
                            onChange={(e) => setExpenseNote(e.target.value)}
                            placeholder="Add any extra details, comments on rejection, or answers to admin questions..."
                            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 resize-y"
                            rows={3}
                            disabled={loading}
                        />
                    </div>
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        onClick={onBack}
                        disabled={loading}
                        className="flex-1 bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition-colors shadow-sm disabled:bg-gray-100 disabled:text-gray-400 flex items-center justify-center gap-2"
                    >
                        Cancel
                    </button>
                    {isEditMode && onDeleteExpense && (
                        <button
                            onClick={onDeleteExpense}
                            disabled={loading}
                            className="bg-white text-red-600 border border-red-200 py-3 px-4 rounded-xl font-bold hover:bg-red-50 hover:border-red-300 transition-colors shadow-sm disabled:bg-gray-100 disabled:text-gray-400 flex items-center justify-center"
                            title="Delete Expense"
                        >
                            <Trash2 size={24} />
                        </button>
                    )}
                    <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 bg-orange-600 text-white py-3 px-4 rounded-xl font-bold text-lg hover:bg-orange-700 transition-colors shadow-sm disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Expense' : 'Add Expense')}
                </button>
            </div>
            </div >
        </div >
    );
};

export default AddExpense;
